import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AccountingAuditReport from '../../components/AccountingAuditReport';
import ProjectCalculationAuditReport from '../../components/ProjectCalculationAuditReport';
import ProcoreSyncReport from '../../components/ProcoreSyncReport';
import JournalEntries from '../../components/JournalEntries';
import { asList, getJson, money, percent, periodLabel } from '../helpers';
import { CLARET_REPORTS, reportPath } from '../reports';
import ClaretReportPage from './ClaretReportPage';
import ProjectAnalysisReport from './ProjectAnalysisReport';
import WipReportActions from './WipReportActions';
import { feeLevelLabel, usesFeeCostCodeLine } from '../../utils/contractFees';

function sortPeriods(periods) {
  return [...periods].sort((a, b) => {
    if (a.status === 'open' && b.status !== 'open') return -1;
    if (a.status !== 'open' && b.status === 'open') return 1;
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });
}

function budgetAmount(row, eacEnabled) {
  return eacEnabled && row.estimated_at_completion
    ? row.estimated_at_completion
    : row.current_budget_amount || 0;
}

function profitAmount(row, eacEnabled) {
  return (row.total_contract_amount || 0) - budgetAmount(row, eacEnabled);
}

function isContractItemFeeBreakdown(detailedBreakdown) {
  const percentComplete = detailedBreakdown?.percent_complete;
  return Boolean(
    percentComplete &&
    typeof percentComplete === 'object' &&
    (percentComplete.total_costs_with_fees != null || Array.isArray(percentComplete.cost_codes_breakdown))
  );
}

function InfoButton({ onClick }) {
  const handle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'click') onClick(event);
  };
  return (
    <button
      type="button"
      className="pc-info-btn"
      title="Show calculation details"
      onMouseDown={handle}
      onClick={handle}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5M12 8h.01" />
      </svg>
    </button>
  );
}

function WipReport({ projectVuid }) {
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState('');
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [eacEnabled, setEacEnabled] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calculationData, setCalculationData] = useState(null);
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getJson('/api/accounting-periods').catch(() => []),
      getJson('/api/wip-settings').catch(() => []),
      projectVuid ? getJson(`/api/projects/${projectVuid}`).catch(() => null) : Promise.resolve(null),
    ]).then(([periodData, settingsData, project]) => {
      if (cancelled) return;
      const sorted = sortPeriods(asList(periodData));
      setPeriods(sorted);
      const openPeriod = sorted.find((period) => period.status === 'open');
      setSelectedPeriod((openPeriod || sorted[0] || {}).vuid || '');
      const eacSetting = asList(settingsData).find((setting) => setting.setting_name === 'use_eac_reporting');
      setEacEnabled(Boolean(eacSetting && eacSetting.setting_value === 'true'));
      if (project?.project_name) setProjectName(project.project_name);
      if (!(openPeriod || sorted[0])) setLoading(false);
    }).catch((err) => {
      if (!cancelled) {
        setError(err.response?.data?.error || 'Unable to load report setup');
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    if (!selectedPeriod) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    const params = { accounting_period_vuid: selectedPeriod };
    if (projectVuid) params.project_vuids = projectVuid;
    getJson('/api/wip', params)
      .then((data) => {
        if (cancelled) return;
        setRows(asList(data));
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Unable to load WIP report');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPeriod, projectVuid, reloadToken]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row.project_number, row.project_name, row.contract_number, row.contract_name, row.customer_name]
        .some((value) => String(value || '').toLowerCase().includes(term))
    );
  }, [rows, query]);

  const projectCalculations = useMemo(
    () => rows.flatMap((row) => (row.project_calculations || []).map((calculation) => ({
      ...calculation,
      project_vuid: row.project_vuid,
    }))),
    [rows],
  );
  const calculationJournals = useMemo(
    () => rows.flatMap((row) => row.calculation_journals || []),
    [rows],
  );

  const totals = useMemo(() => {
    return visible.reduce(
      (sum, row) => {
        sum.original_contract_amount += Number(row.original_contract_amount || 0);
        sum.change_orders += Number(row.change_orders || 0);
        sum.total_contract_amount += Number(row.total_contract_amount || 0);
        sum.original_budget_amount += Number(row.original_budget_amount || 0);
        sum.budget += Number(budgetAmount(row, eacEnabled));
        sum.costs_to_date += Number(row.costs_to_date || 0);
        sum.project_billings += Number(row.project_billings || 0);
        sum.revenue_recognized += Number(row.revenue_recognized || row.earned_revenue || 0);
        sum.profit += profitAmount(row, eacEnabled);
        sum.over_billing += Number(row.over_billing || 0);
        sum.under_billing += Number(row.under_billing || 0);
        return sum;
      },
      {
        original_contract_amount: 0,
        change_orders: 0,
        total_contract_amount: 0,
        original_budget_amount: 0,
        budget: 0,
        costs_to_date: 0,
        project_billings: 0,
        revenue_recognized: 0,
        profit: 0,
        over_billing: 0,
        under_billing: 0,
      }
    );
  }, [visible, eacEnabled]);

  const selected = periods.find((period) => period.vuid === selectedPeriod);
  const openProject = (row) => {
    if (row?.project_vuid) navigate(`/procore/projects/${row.project_vuid}`);
  };

  const closeCalculationModal = () => {
    setShowCalculationModal(false);
    setCalculationData(null);
  };

  const handleShowCalculationDetails = (event, row) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = {
      project_number: row.project_number,
      project_name: row.project_name,
      costs_to_date: row.costs_to_date || 0,
      budget_value: budgetAmount(row, eacEnabled),
      budget_label: eacEnabled && row.estimated_at_completion ? 'Estimated Cost at Completion (EAC)' : 'Current Budget',
      total_contract_amount: row.total_contract_amount || 0,
      percent_complete: row.percent_complete || 0,
      revenue_recognized: row.revenue_recognized || row.earned_revenue || 0,
      uses_contract_item_fee_level: Boolean(row.uses_contract_item_fee_level),
      uses_fee_cost_code: Boolean(row.uses_fee_cost_code),
      fee_level: row.fee_level || null,
      detailed_breakdown: null,
    };
    setCalculationData(payload);
    setShowCalculationModal(true);
    if (!row.project_vuid) return;
    getJson(`/api/wip/${row.project_vuid}/calculation-details`, {
      accounting_period_vuid: selectedPeriod || undefined,
    })
      .then((detailedBreakdown) => {
        const hasFeeBreakdown = isContractItemFeeBreakdown(detailedBreakdown);
        setCalculationData((current) => {
          if (!current || current.project_number !== row.project_number) return current;
          return {
            ...current,
            uses_contract_item_fee_level: hasFeeBreakdown,
            detailed_breakdown: hasFeeBreakdown ? detailedBreakdown : current.detailed_breakdown,
          };
        });
      })
      .catch((err) => {
        console.error('Error fetching calculation details:', err);
      });
  };

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            <Link to={reportPath(projectVuid)}>Reports</Link>
            {' / '}
            WIP Report
          </>
        ) : (
          <>
            <Link to={reportPath()}>Company / Reports</Link>
            {' / '}
            WIP Report
          </>
        )}
      </div>
      <div className="pc-title-row">
        <h1>{projectVuid ? 'Project WIP Report' : 'WIP Report'}</h1>
        <WipReportActions
          selectedPeriod={selectedPeriod}
          period={selected}
          rows={rows}
          projectVuid={projectVuid}
          eacEnabled={eacEnabled}
          onRefresh={async () => {
            try {
              setPeriods(sortPeriods(asList(await getJson('/api/accounting-periods'))));
            } catch (err) {
              // Keep the current period list if refresh fails.
            }
            setReloadToken((value) => value + 1);
          }}
        />
      </div>
      <div className="pc-toolbar">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={projectVuid ? 'Filter this project' : 'Filter projects'}
        />
        <label className="pc-toolbar-label">
          As of period
          <select value={selectedPeriod} onChange={(event) => setSelectedPeriod(event.target.value)}>
            {periods.length === 0 ? <option value="">No periods</option> : null}
            {periods.map((period) => (
              <option key={period.vuid} value={period.vuid}>
                {periodLabel(period)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {selectedPeriod && calculationJournals.length ? (
        <div className="pc-journal-alert">
          <div>Project Calculations ready to post</div>
          <ul>
            {calculationJournals.map((item) => (
              <li key={item.run_vuid || item.assignment_vuid}>
                {item.project_number} {item.definition_name} — {item.amount_display || money(item.amount || 0)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {selectedPeriod && projectCalculations.length ? (
        <div className="pc-journal-alert">
          <div>Project Calculations</div>
          <ul>
            {projectCalculations.map((item) => {
              const blocking = !item.satisfied && !item.will_post;
              return (
                <li key={item.assignment_vuid}>
                  <Link to={`/procore/projects/${item.project_vuid}/calculations/${item.assignment_vuid}`}>
                    {blocking ? '✗' : '✓'} {item.definition_name}
                  </Link>
                  <span>
                    {' — '}
                    {item.label}
                    {item.status === 'ready' || item.status === 'posted' ? ` — ${item.amount_display || money(item.amount || 0)}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <div className="pc-card pc-report-card">
        {loading ? (
          <div className="pc-empty">Loading WIP report…</div>
        ) : error ? (
          <div className="pc-empty">{error}</div>
        ) : visible.length === 0 ? (
          <div className="pc-empty">No WIP data for this period.</div>
        ) : (
          <>
          <div className="pc-table-scroll">
            <table className="pc-table pc-report">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Contract</th>
                  <th className="pc-right">Original Amount</th>
                  <th className="pc-right">Change Orders</th>
                  <th className="pc-right">Total Contract</th>
                  <th className="pc-right">Original Budget</th>
                  <th className="pc-right">{eacEnabled ? 'Est. Cost at Completion' : 'Current Budget'}</th>
                  <th className="pc-right">Costs to Date</th>
                  <th className="pc-right">Billed to Date</th>
                  <th className="pc-center">% Complete</th>
                  <th className="pc-right">Earned Revenue</th>
                  <th className="pc-center">Profit Margin %</th>
                  <th className="pc-right">Profit</th>
                  <th className="pc-right">Overbilled</th>
                  <th className="pc-right">Underbilled</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const profit = profitAmount(row, eacEnabled);
                  return (
                    <tr
                      key={row.project_vuid || row.contract_number}
                      className="pc-clickable"
                      onClick={() => openProject(row)}
                    >
                      <td>
                        {row.project_vuid ? (
                          <Link
                            to={`/procore/projects/${row.project_vuid}`}
                            onClick={(event) => event.stopPropagation()}
                          >
                            {row.project_number || '—'}
                          </Link>
                        ) : (
                          row.project_number || '—'
                        )}
                        <div className="pc-sub">{row.project_name}</div>
                      </td>
                      <td>
                        {row.contract_number || '—'}
                        <div className="pc-sub">{row.contract_name}</div>
                        {row.customer_name ? <div className="pc-sub">{row.customer_name}</div> : null}
                      </td>
                      <td className="pc-right">{money(row.original_contract_amount)}</td>
                      <td className="pc-right">
                        {money(row.change_orders)}
                        {row.includes_forecast_pending_cos ? '*' : ''}
                      </td>
                      <td className="pc-right">{money(row.total_contract_amount)}</td>
                      <td className="pc-right">{money(row.original_budget_amount)}</td>
                      <td className="pc-right">
                        {money(budgetAmount(row, eacEnabled))}
                        {row.uses_fee_cost_code ? '†' : ''}
                      </td>
                      <td className="pc-right">{money(row.costs_to_date)}</td>
                      <td className="pc-right">{money(row.project_billings)}</td>
                      <td className="pc-center">
                        <div className="pc-calc-cell">
                          <span className="pc-pill">{percent(row.percent_complete)}</span>
                          <InfoButton onClick={(event) => handleShowCalculationDetails(event, row)} />
                        </div>
                      </td>
                      <td className="pc-right">
                        <div className="pc-calc-cell pc-calc-cell-right">
                          {money(row.revenue_recognized || row.earned_revenue)}
                          <InfoButton onClick={(event) => handleShowCalculationDetails(event, row)} />
                        </div>
                      </td>
                      <td className="pc-center">
                        <span className="pc-pill">{percent(row.profit_margin_percent)}</span>
                      </td>
                      <td className={`pc-right ${profit > 0 ? 'pc-pos' : profit < 0 ? 'pc-neg' : ''}`}>
                        {money(profit)}
                      </td>
                      <td className="pc-right">{money(row.over_billing)}</td>
                      <td className="pc-right">{money(row.under_billing)}</td>
                    </tr>
                  );
                })}
              </tbody>
              {visible.length > 1 ? (
                <tfoot>
                  <tr>
                    <td colSpan={2}>Totals ({visible.length})</td>
                    <td className="pc-right">{money(totals.original_contract_amount)}</td>
                    <td className="pc-right">
                      {money(totals.change_orders)}
                      {visible.some((row) => row.includes_forecast_pending_cos) ? '*' : ''}
                    </td>
                    <td className="pc-right">{money(totals.total_contract_amount)}</td>
                    <td className="pc-right">{money(totals.original_budget_amount)}</td>
                    <td className="pc-right">{money(totals.budget)}</td>
                    <td className="pc-right">{money(totals.costs_to_date)}</td>
                    <td className="pc-right">{money(totals.project_billings)}</td>
                    <td />
                    <td className="pc-right">{money(totals.revenue_recognized)}</td>
                    <td />
                    <td className={`pc-right ${totals.profit > 0 ? 'pc-pos' : totals.profit < 0 ? 'pc-neg' : ''}`}>
                      {money(totals.profit)}
                    </td>
                    <td className="pc-right">{money(totals.over_billing)}</td>
                    <td className="pc-right">{money(totals.under_billing)}</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
          {visible.some((row) => row.includes_forecast_pending_cos) ? (
            <p className="pc-report-note">
              * Change Orders include pending PCOs selected in Revenue Forecasting for this period.
            </p>
          ) : null}
          {visible.some((row) => row.uses_fee_cost_code) ? (
            <p className="pc-report-note">
              † Original budget amounts and EAC do not include amounts budgeted to the dedicated fee cost code.
            </p>
          ) : null}
          </>
        )}
      </div>
      {showCalculationModal && calculationData
        ? createPortal(
        <div className="pc-modal-backdrop" onClick={closeCalculationModal}>
          <div className="pc-modal" onClick={(event) => event.stopPropagation()}>
            <div className="pc-modal-head">
              <h2>Calculation Details</h2>
              <button type="button" className="pc-modal-close" onClick={closeCalculationModal} aria-label="Close">
                ×
              </button>
            </div>
            <h3 className="pc-modal-project">
              {calculationData.project_number} — {calculationData.project_name}
            </h3>

            <section className="pc-calc-panel pc-calc-blue">
              <h4>% Complete Calculation</h4>
              {calculationData.uses_contract_item_fee_level ? (
                <>
                  <p className="pc-calc-method">{feeLevelLabel('contract_item')} Method</p>
                  <p>Percent complete uses only cost codes that have fees:</p>
                  <p className="pc-mono">
                    (Costs for cost codes with fees ÷ Budget for cost codes with fees) × 100
                  </p>
                  {calculationData.detailed_breakdown?.percent_complete ? (
                    <>
                      <div className="pc-kv-row">
                        <span>Total Costs (with fees)</span>
                        <strong>{money(calculationData.detailed_breakdown.percent_complete.total_costs_with_fees)}</strong>
                      </div>
                      <div className="pc-kv-row">
                        <span>Total Budget (with fees)</span>
                        <strong>{money(calculationData.detailed_breakdown.percent_complete.total_budget_with_fees)}</strong>
                      </div>
                      <p className="pc-mono">
                        ({money(calculationData.detailed_breakdown.percent_complete.total_costs_with_fees)} ÷ {money(calculationData.detailed_breakdown.percent_complete.total_budget_with_fees)}) × 100 = {percent(calculationData.detailed_breakdown.percent_complete.project_percent_complete)}
                      </p>
                      {(calculationData.detailed_breakdown.percent_complete.cost_codes_breakdown || []).length > 0 ? (
                        <div className="pc-calc-table-wrap">
                          <table className="pc-calc-table">
                            <thead>
                              <tr>
                                <th>Cost Code</th>
                                <th className="pc-right">Costs</th>
                                <th className="pc-right">Budget</th>
                              </tr>
                            </thead>
                            <tbody>
                              {calculationData.detailed_breakdown.percent_complete.cost_codes_breakdown.map((item, index) => (
                                <tr key={index}>
                                  <td>
                                    {item.cost_code}
                                    <div className="pc-sub">{item.cost_code_description}</div>
                                  </td>
                                  <td className="pc-right">{money(item.costs)}</td>
                                  <td className="pc-right">{money(item.budget)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                      <div className="pc-kv-row pc-kv-total">
                        <span>% Complete</span>
                        <strong>{percent(calculationData.detailed_breakdown.percent_complete.project_percent_complete)}</strong>
                      </div>
                    </>
                  ) : (
                    <div className="pc-kv-row pc-kv-total">
                      <span>% Complete</span>
                      <strong>{percent(calculationData.percent_complete)}</strong>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {usesFeeCostCodeLine(calculationData.fee_level) ? (
                    <p className="pc-calc-method">{feeLevelLabel(calculationData.fee_level)} Method</p>
                  ) : null}
                  <div className="pc-kv-row">
                    <span>Costs to Date</span>
                    <strong>{money(calculationData.costs_to_date)}</strong>
                  </div>
                  <div className="pc-kv-row">
                    <span>{calculationData.budget_label}</span>
                    <strong>{money(calculationData.budget_value)}</strong>
                  </div>
                  <div className="pc-kv-row">
                    <span>Formula</span>
                    <span className="pc-mono">(Costs to Date ÷ Budget) × 100</span>
                  </div>
                  <p className="pc-mono">
                    ({money(calculationData.costs_to_date)} ÷ {money(calculationData.budget_value)}) × 100
                  </p>
                  <div className="pc-kv-row pc-kv-total">
                    <span>% Complete</span>
                    <strong>{percent(calculationData.percent_complete)}</strong>
                  </div>
                </>
              )}
            </section>

            <section className="pc-calc-panel pc-calc-green">
              <h4>Earned Revenue Calculation</h4>
              {calculationData.uses_contract_item_fee_level ? (
                <>
                  <p className="pc-calc-method">{feeLevelLabel('contract_item')} Method</p>
                  <ul>
                    <li>If fee exists: Total Fee = Budget × Fee %</li>
                    <li>
                      Earned Fee = Total Fee × Project % Complete (
                      {calculationData.detailed_breakdown?.revenue_recognized?.project_percent_complete != null
                        ? percent(calculationData.detailed_breakdown.revenue_recognized.project_percent_complete)
                        : 'N/A'}
                      )
                    </li>
                    <li>Revenue = Actual Cost + Earned Fee</li>
                    <li>Total Revenue = Sum of all cost code / cost type combinations</li>
                  </ul>
                  {(calculationData.detailed_breakdown?.revenue_recognized?.breakdown || []).length > 0 ? (
                    <div className="pc-calc-table-wrap">
                      <table className="pc-calc-table">
                        <thead>
                          <tr>
                            <th>Cost Code</th>
                            <th>Cost Type</th>
                            <th className="pc-right">Budget</th>
                            <th className="pc-right">Fee %</th>
                            <th className="pc-right">Total Fee</th>
                            <th className="pc-right">Actual Cost</th>
                            <th className="pc-right">Earned Fee</th>
                            <th className="pc-right">Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {calculationData.detailed_breakdown.revenue_recognized.breakdown.map((item, index) => (
                            <tr key={index}>
                              <td>
                                {item.cost_code}
                                <div className="pc-sub">{item.cost_code_description}</div>
                              </td>
                              <td>{item.cost_type}</td>
                              <td className="pc-right">{money(item.budget)}</td>
                              <td className="pc-right">{item.fee_percentage > 0 ? `${Number(item.fee_percentage).toFixed(2)}%` : '—'}</td>
                              <td className="pc-right">{item.total_fee > 0 ? money(item.total_fee) : '—'}</td>
                              <td className="pc-right">{money(item.actual_cost)}</td>
                              <td className="pc-right">{item.earned_fee > 0 ? money(item.earned_fee) : '—'}</td>
                              <td className="pc-right">{money(item.revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                  <div className="pc-kv-row pc-kv-total">
                    <span>Earned Revenue</span>
                    <strong>
                      {money(
                        calculationData.detailed_breakdown?.revenue_recognized?.total_revenue ??
                        calculationData.revenue_recognized
                      )}
                    </strong>
                  </div>
                </>
              ) : (
                <>
                  {usesFeeCostCodeLine(calculationData.fee_level) ? (
                    <p className="pc-calc-method">{feeLevelLabel(calculationData.fee_level)} Method</p>
                  ) : null}
                  <div className="pc-kv-row">
                    <span>% Complete</span>
                    <strong>{percent(calculationData.percent_complete)}</strong>
                  </div>
                  <div className="pc-kv-row">
                    <span>Total Contract Amount</span>
                    <strong>{money(calculationData.total_contract_amount)}</strong>
                  </div>
                  <div className="pc-kv-row">
                    <span>Formula</span>
                    <span className="pc-mono">(Costs to Date ÷ Budget) × Total Contract</span>
                  </div>
                  <p className="pc-mono">
                    ({money(calculationData.costs_to_date)} ÷ {money(calculationData.budget_value)}) × {money(calculationData.total_contract_amount)}
                  </p>
                  <div className="pc-kv-row pc-kv-total">
                    <span>Earned Revenue</span>
                    <strong>{money(calculationData.revenue_recognized)}</strong>
                  </div>
                </>
              )}
            </section>

            <div className="pc-modal-actions">
              <button type="button" className="pc-btn-secondary" onClick={closeCalculationModal}>
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
}

function ReportsCatalog({ projectVuid }) {
  const [projectName, setProjectName] = useState('');
  useEffect(() => {
    if (!projectVuid) return undefined;
    let cancelled = false;
    getJson(`/api/projects/${projectVuid}`)
      .then((project) => {
        if (!cancelled && project?.project_name) setProjectName(project.project_name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            Reports
          </>
        ) : (
          'Company / Reports'
        )}
      </div>
      <div className="pc-title-row">
        <h1>Reports</h1>
      </div>
      <div className="pc-report-list">
        {CLARET_REPORTS.map((report) => (
          <Link key={report.id} className="pc-report-item" to={reportPath(projectVuid, report.id)}>
            <strong>{report.name}</strong>
            <span>{report.description}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function JournalEntriesReport({ projectVuid }) {
  const [projectName, setProjectName] = useState('');
  useEffect(() => {
    if (!projectVuid) return undefined;
    let cancelled = false;
    getJson(`/api/projects/${projectVuid}`)
      .then((project) => {
        if (!cancelled && project?.project_name) setProjectName(project.project_name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            <Link to={reportPath(projectVuid)}>Reports</Link>
            {' / '}
            Journal Entries
          </>
        ) : (
          <>
            <Link to={reportPath()}>Company / Reports</Link>
            {' / '}
            Journal Entries
          </>
        )}
      </div>
      <div className="pc-je-embed">
        <JournalEntries />
      </div>
    </div>
  );
}

function ProcoreSyncReportEmbed({ projectVuid }) {
  const [projectName, setProjectName] = useState('');
  useEffect(() => {
    if (!projectVuid) return undefined;
    let cancelled = false;
    getJson(`/api/projects/${projectVuid}`)
      .then((project) => {
        if (!cancelled && project?.project_name) setProjectName(project.project_name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            <Link to={reportPath(projectVuid)}>Reports</Link>
            {' / '}
            Procore Sync Report
          </>
        ) : (
          <>
            <Link to={reportPath()}>Company / Reports</Link>
            {' / '}
            Procore Sync Report
          </>
        )}
      </div>
      <div className="pc-je-embed">
        <ProcoreSyncReport initialProjectVuid={projectVuid} />
      </div>
    </div>
  );
}

function CalculationAuditReportEmbed({ projectVuid }) {
  const [projectName, setProjectName] = useState('');
  useEffect(() => {
    if (!projectVuid) return undefined;
    let cancelled = false;
    getJson(`/api/projects/${projectVuid}`)
      .then((project) => {
        if (!cancelled && project?.project_name) setProjectName(project.project_name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            <Link to={reportPath(projectVuid)}>Reports</Link>
            {' / '}
            Project Calculations Audit
          </>
        ) : (
          <>
            <Link to={reportPath()}>Company / Reports</Link>
            {' / '}
            Project Calculations Audit
          </>
        )}
      </div>
      <div className="pc-je-embed">
        <ProjectCalculationAuditReport initialProjectVuid={projectVuid} />
      </div>
    </div>
  );
}

function AccountingAuditReportEmbed({ projectVuid }) {
  const [projectName, setProjectName] = useState('');
  useEffect(() => {
    if (!projectVuid) return undefined;
    let cancelled = false;
    getJson(`/api/projects/${projectVuid}`)
      .then((project) => {
        if (!cancelled && project?.project_name) setProjectName(project.project_name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            <Link to={reportPath(projectVuid)}>Reports</Link>
            {' / '}
            Accounting Audit Report
          </>
        ) : (
          <>
            <Link to={reportPath()}>Company / Reports</Link>
            {' / '}
            Accounting Audit Report
          </>
        )}
      </div>
      <div className="pc-je-embed">
        <AccountingAuditReport initialProjectVuid={projectVuid} />
      </div>
    </div>
  );
}

export default function ReportsPage({ projectVuid }) {
  const { reportId } = useParams();
  if (!reportId) return <ReportsCatalog projectVuid={projectVuid} />;
  if (reportId === 'wip') return <WipReport projectVuid={projectVuid} />;
  if (reportId === 'project-analysis') return <ProjectAnalysisReport projectVuid={projectVuid} />;
  if (reportId === 'journal-entries') return <JournalEntriesReport projectVuid={projectVuid} />;
  if (reportId === 'audit') return <AccountingAuditReportEmbed projectVuid={projectVuid} />;
  if (reportId === 'calculation-audit') return <CalculationAuditReportEmbed projectVuid={projectVuid} />;
  if (reportId === 'procore-sync') return <ProcoreSyncReportEmbed projectVuid={projectVuid} />;
  return <ClaretReportPage reportId={reportId} projectVuid={projectVuid} />;
}
